import type {
  BanRecord,
  Battle,
  GoldHistoryEntry,
  InGameMail,
  Player,
  PlayerCard,
  PlayerRole,
  PlayerStatus,
  PlayerTransaction,
  Rarity,
} from '@/models'

const NAMES = [
  'NovaBlitz',
  'IronPeach',
  'Sparrowhawk',
  'MudKing',
  'VoltGoblin',
  'SilentAsh',
  'CrimsonYeti',
  'LunaFang',
  'TurboSnail',
  'GrimTide',
  'PixelPaladin',
  'FrostByte',
  'ZenArrow',
  'HexRider',
  'OldMate42',
  'QuietStorm',
  'BarrelRoll',
  'MintCondition',
  'DustDevil',
  'KoiEmperor',
  'NightShift',
  'PaperCrane',
  'StaticMoth',
  'RiverStone',
  'GoldLeaf',
  'WolfTax',
  'EchoNine',
  'SaltFlat',
  'CinderTop',
  'BlueLarch',
  'MossGiant',
  'TinLantern',
  'FableFox',
  'GhostPepper',
  'WrenKeeper',
  'OnyxDrift',
  'MapleRush',
  'CobaltOwl',
  'SableMarch',
  'HollowPine',
  'GaleWarden',
  'EmberSill',
  'TorchCarp',
  'BriarKnot',
  'FenWalker',
  'LarkSpur',
  'StoneToll',
  'VeldtRunner',
  'MirrorCarp',
  'AshGrove',
]
const COUNTRIES = ['US', 'KR', 'DE', 'BR', 'JP', 'FR', 'GB', 'MX', 'IN', 'SE', 'TR', 'CA']
const STATUSES: PlayerStatus[] = [
  'Active',
  'Active',
  'Active',
  'Active',
  'Active',
  'Active',
  'Active',
  'Suspended',
  'Active',
  'Active',
  'Banned',
  'Active',
]
const ROLES: PlayerRole[] = ['Player', 'Player', 'Player', 'Moderator', 'VIP Admin', 'Tester']

/* Arithmetic rather than random, so the list is identical on every reload and
 * a bug found on row 34 is still on row 34 after a refresh. */
export const PLAYERS: Player[] = NAMES.map((username, i) => ({
  id: '3VF' + (6900 + i * 3).toString(16).toUpperCase(),
  username,
  level: 1 + ((i * 7) % 13),
  exp: 200 + ((i * 45) % 1800),
  /* Half the fixtures have no bio on purpose, so the empty state is reachable. */
  bio: i % 2 === 0 ? undefined : 'Hardcore player on the Roundhouse ladder.',
  trophies: 800 + ((i * 613) % 6400),
  rank: i + 1,
  role: ROLES[i % ROLES.length],
  svipLevel: i % 4 === 0 ? 3 : i % 3 === 0 ? 2 : i % 2 === 0 ? 1 : 0,
  status: STATUSES[i % STATUSES.length],
  country: COUNTRIES[i % COUNTRIES.length],
  lastSeenMin: (i * 47) % 4300,
  joined: `2026-${String(1 + (i % 12)).padStart(2, '0')}-${String(1 + ((i * 3) % 28)).padStart(2, '0')}`,
  gold: 810992 - i * 1540,
  gems: (i * 53) % 4000,
  cara: 3 + (i % 12),
  silver: 40000 + ((i * 1234) % 90000),
  babich: (i * 7) % 50,
  staminaRank: 0,
  staminaSolo: 0,
  referralCode: i % 3 === 0 ? 'REF-' + (8000 + i) : 'none',
  referralUrl: i % 3 === 0 ? 'https://roundhouse.game/r/' + (8000 + i) : 'none',
  wins: 40 + ((i * 31) % 900),
  losses: 30 + ((i * 23) % 700),
}))

const RARITIES: Rarity[] = ['Common', 'Rare', 'Epic', 'Legendary']
const DECK = [
  'Ember Imp',
  'Shield Rat',
  'Frost Hare',
  'Iron Boar',
  'Storm Colossus',
  'Night Chorus',
  'Sun Anchor',
  'Void Antler',
  'Royal Knight',
  'Sky Archers',
  'Baby Dragon',
  'Valkyrie Blade',
  'Mini Mech',
  'Musketeer',
  'Dark Prince',
  'Electro Colossus',
]

const DECK_IMAGES: Record<string, string> = {
  'Ember Imp':
    'https://raw.githubusercontent.com/RoyaleAPI/cr-api-assets/master/cards/fire-spirit.png',
  'Shield Rat': 'https://raw.githubusercontent.com/RoyaleAPI/cr-api-assets/master/cards/guards.png',
  'Frost Hare':
    'https://raw.githubusercontent.com/RoyaleAPI/cr-api-assets/master/cards/ice-spirit.png',
  'Iron Boar':
    'https://raw.githubusercontent.com/RoyaleAPI/cr-api-assets/master/cards/hog-rider.png',
  'Storm Colossus':
    'https://raw.githubusercontent.com/RoyaleAPI/cr-api-assets/master/cards/mega-knight.png',
  'Night Chorus':
    'https://raw.githubusercontent.com/RoyaleAPI/cr-api-assets/master/cards/witch.png',
  'Sun Anchor': 'https://raw.githubusercontent.com/RoyaleAPI/cr-api-assets/master/cards/pekka.png',
  'Void Antler':
    'https://raw.githubusercontent.com/RoyaleAPI/cr-api-assets/master/cards/inferno-dragon.png',
  'Royal Knight':
    'https://raw.githubusercontent.com/RoyaleAPI/cr-api-assets/master/cards/knight.png',
  'Sky Archers':
    'https://raw.githubusercontent.com/RoyaleAPI/cr-api-assets/master/cards/archers.png',
  'Baby Dragon':
    'https://raw.githubusercontent.com/RoyaleAPI/cr-api-assets/master/cards/baby-dragon.png',
  'Valkyrie Blade':
    'https://raw.githubusercontent.com/RoyaleAPI/cr-api-assets/master/cards/valkyrie.png',
  'Mini Mech':
    'https://raw.githubusercontent.com/RoyaleAPI/cr-api-assets/master/cards/mini-pekka.png',
  Musketeer: 'https://raw.githubusercontent.com/RoyaleAPI/cr-api-assets/master/cards/musketeer.png',
  'Dark Prince':
    'https://raw.githubusercontent.com/RoyaleAPI/cr-api-assets/master/cards/prince.png',
  'Electro Colossus':
    'https://raw.githubusercontent.com/RoyaleAPI/cr-api-assets/master/cards/electro-giant.png',
}

export function cardsForPlayer(player: Player): PlayerCard[] {
  return DECK.map((name, i) => ({
    name,
    rarity: RARITIES[(player.level + i) % RARITIES.length],
    level: 1 + ((player.wins + i * 3) % 13),
    image: DECK_IMAGES[name],
  }))
}

export function battlesForPlayer(player: Player): Battle[] {
  return Array.from({ length: 6 }, (_, i) => {
    const win = (player.wins + i) % 3 !== 0
    return {
      id: `${player.id}-b${i}`,
      opponent: NAMES[(player.level * 5 + i * 7) % NAMES.length],
      result: win ? ('Win' as const) : ('Loss' as const),
      crowns: win ? `${1 + (i % 3)} – ${i % 2}` : `${i % 2} – ${1 + (i % 3)}`,
      ago: `${1 + ((player.gems + i * 13) % 46)}h ago`,
    }
  })
}

export function transactionsForPlayer(player: Player): PlayerTransaction[] {
  const PACKS = [
    { name: 'Starter Pouch', amount: 4.99 },
    { name: 'Season Pass Pro', amount: 9.99 },
    { name: 'Pile of Cara Gems', amount: 19.99 },
    { name: 'Chest of Gold', amount: 49.99 },
    { name: 'Ultimate SVIP Bundle', amount: 99.99 },
  ]
  const CHANNELS: ('App Store' | 'Google Play' | 'Stripe')[] = [
    'App Store',
    'Google Play',
    'Stripe',
  ]
  const STATUSES: ('Completed' | 'Pending' | 'Refunded')[] = [
    'Completed',
    'Completed',
    'Completed',
    'Refunded',
    'Completed',
  ]

  return Array.from({ length: 5 }, (_, i) => {
    const pack = PACKS[(player.level + i) % PACKS.length]
    return {
      id: `tx_${player.id}_00${i + 1}`,
      packName: pack.name,
      amount: pack.amount,
      currency: 'USD',
      channel: CHANNELS[(i + player.rank) % CHANNELS.length],
      status: STATUSES[(i + player.svipLevel) % STATUSES.length],
      timestamp: `2026-08-${String(26 - i * 3).padStart(2, '0')} ${10 + i}:15`,
    }
  })
}

export function goldHistoryForPlayer(player: Player): GoldHistoryEntry[] {
  const ACTIONS = [
    { type: 'Gold' as const, desc: 'Ranked Battle Win Reward', change: '+1,200', pos: true },
    { type: 'Gold' as const, desc: 'Card Level Upgrade (Ember Imp)', change: '-3,500', pos: false },
    { type: 'Cara' as const, desc: 'Season Pass Level Milestone', change: '+250', pos: true },
    { type: 'Cara' as const, desc: 'Purchased Legendary Chest', change: '-800', pos: false },
    { type: 'Gold' as const, desc: 'Daily Login Streak Bonus', change: '+5,000', pos: true },
    { type: 'Cara' as const, desc: 'SVIP Weekly Allowance', change: '+500', pos: true },
  ]

  let runningGold = player.gold
  return ACTIONS.map((act, i) => {
    runningGold = act.pos ? runningGold - 500 : runningGold + 2000
    return {
      id: `gh_${player.id}_${i}`,
      currencyType: act.type,
      action: act.desc,
      change: act.change,
      isPositive: act.pos,
      balanceAfter: runningGold,
      timestamp: `${1 + i * 4}h ago`,
    }
  })
}

export function mailsForPlayer(player: Player): InGameMail[] {
  return [
    {
      id: `mail_${player.id}_1`,
      title: 'Season 12 Placement Rewards Distributed!',
      sender: 'System Admin',
      read: true,
      rewards: '10,000 Gold • 300 Cara • Season Badge',
      sentAt: '2 days ago',
    },
    {
      id: `mail_${player.id}_2`,
      title: 'SVIP Monthly Benefit Pack',
      sender: 'VIP Concierge',
      read: true,
      rewards: '25,000 Gold • 500 Cara',
      sentAt: '5 days ago',
    },
    {
      id: `mail_${player.id}_3`,
      title: 'Maintenance Compensation Gift',
      sender: 'Roundhouse Ops',
      read: false,
      rewards: '2,000 Gold • 100 Cara',
      sentAt: '1 week ago',
    },
  ]
}

export function banHistoryForPlayer(player: Player): BanRecord[] {
  if (player.status === 'Banned') {
    return [
      {
        id: 'BAN-9921',
        action: 'Banned',
        reason: 'Repeated speed hack & game memory manipulation detected by Anti-Cheat system.',
        operator: 'System Anti-Cheat v2',
        timestamp: '2026-08-20 14:32:10',
        expiresAt: 'Permanent',
      },
      {
        id: 'BAN-8812',
        action: 'Warned',
        reason: 'Inappropriate language in public match chat.',
        operator: 'mod_alex',
        timestamp: '2026-06-12 09:15:00',
      },
    ]
  }

  return [
    {
      id: 'BAN-7704',
      action: 'Unbanned',
      reason: 'Appeal approved after manual video review of network lag sync artifact.',
      operator: 'admin_david',
      timestamp: '2026-05-10 18:45:00',
    },
    {
      id: 'BAN-7690',
      action: 'Banned',
      reason: 'Automated flag: Suspicious trophy gain rate.',
      operator: 'System Anti-Cheat v2',
      timestamp: '2026-05-08 11:20:00',
      expiresAt: '2026-05-15 (7 Days)',
    },
  ]
}
